import AppComponent from '../base/app-component.ts';
import type { StatiniEsito } from '../lib/api/shared.ts';
import { statiniApi, type RisorsaStatini } from '../lib/api/statini.ts';
import { formatNumero } from '../lib/utils.ts';
import { templateParseToElement } from '../lib/template-parse.ts';

export interface StatoInvioStatini {
  filtri: Record<string, string>;
  abilitato: boolean;
  tooltip: string;
}

const frase = (n: number, uno: string, molti: string): string => (n === 1 ? uno : molti);

/**
 * Aggiorna filtri, abilitazione e tooltip via attributi: funziona anche se il
 * componente non è ancora stato definito (l'upgrade legge gli attributi presenti).
 */
export function aggiornaInvioStatini(el: Element | null, stato: StatoInvioStatini): void {
  if (!el) return;
  el.setAttribute('filtri', JSON.stringify(stato.filtri));
  el.toggleAttribute('abilitato', stato.abilitato);
  el.setAttribute('tooltip', stato.tooltip);
}

/**
 * Bottone "Invia a Statini" con modale mese/anno (anteprima) e modale esito.
 * Attributi: `risorsa` (prefisso API), `staging`, `abilitato`, `tooltip`, `filtri` (JSON).
 * Alla chiusura dell'esito emette `statini-inviati` (bubbles): il genitore ricarica la lista.
 */
export class InvioStatini extends AppComponent {
  static override componentName = 'wc-invio-statini';
  static override observedAttributes = ['abilitato', 'tooltip'];
  protected override get templateId() { return 'invio-statini'; }

  // Modali spostate su body per z-index
  private inviaModalEl: HTMLElement | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private inviaModal: any = null;
  private esitoModalEl: HTMLElement | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private esitoModal: any = null;

  private get risorsa(): RisorsaStatini {
    return this.attributeString('risorsa', 'deleghe') as RisorsaStatini;
  }

  private get filtri(): Record<string, string> {
    try {
      return JSON.parse(this.attributeString('filtri', '{}')) as Record<string, string>;
    } catch {
      return {};
    }
  }

  protected override buildRenderingData(): Record<string, unknown> {
    const annoCorrente = new Date().getFullYear();
    return {
      tooltip: this.attributeString('tooltip'),
      abilitato: this.attributeBoolean('abilitato'),
      annoStatini: annoCorrente,
      anniStatini: [annoCorrente - 1, annoCorrente, annoCorrente + 1],
    };
  }

  protected override afterRender(): void {
    this.setupInviaModal();
    this.setupEsitoModal();
    this.delegate.on('click', '[data-action="invia-statini"]', () => {
      void this.apriInviaStatini();
    });
  }

  /** Il tooltip sta sullo span esterno: un bottone disabilitato non riceve eventi mouse. */
  protected override onAttributeChanged(): void {
    const btn = this.$('[data-action="invia-statini"]') as HTMLButtonElement | null;
    if (btn) btn.disabled = !this.attributeBoolean('abilitato');
    this.$('[data-statini-wrapper]')?.setAttribute('data-tooltip', this.attributeString('tooltip'));
  }

  private setupInviaModal(): void {
    this.inviaModalEl = this.querySelector('[data-modal="invia-statini"]');
    if (!this.inviaModalEl) return;
    document.body.appendChild(this.inviaModalEl);
    this.inviaModal = new (globalThis as any).bootstrap.Modal(this.inviaModalEl);
    const form = this.inviaModalEl.querySelector('[data-form="invia-statini"]') as HTMLFormElement | null;
    if (!form) return;
    this.listen(form, 'submit', (e: Event) => {
      e.preventDefault();
      void this.inviaStatini();
    });
  }

  private setupEsitoModal(): void {
    this.esitoModalEl = this.querySelector('[data-modal="esito-statini"]');
    if (!this.esitoModalEl) return;
    document.body.appendChild(this.esitoModalEl);
    this.esitoModal = new (globalThis as any).bootstrap.Modal(this.esitoModalEl);
    this.listen(this.esitoModalEl, 'hidden.bs.modal', () => this.dispatch('statini-inviati'));
  }

  private inviaEl<T extends HTMLElement>(selector: string): T | null {
    return this.inviaModalEl?.querySelector(selector) as T | null;
  }

  private setRigaModale(selector: string, visibile: boolean, testo: string): void {
    const el = this.inviaEl(selector);
    if (!el) return;
    el.textContent = testo;
    el.classList.toggle('d-none', !visibile);
  }

  private setSubmit(disabled: boolean, label: string, spinner = false): void {
    const submit = this.inviaEl<HTMLButtonElement>('[data-statini-submit]');
    if (submit) submit.disabled = disabled;
    const labelEl = this.inviaEl('[data-statini-submit-label]');
    if (labelEl) labelEl.textContent = label;
    this.inviaEl('[data-statini-spinner]')?.classList.toggle('d-none', !spinner);
  }

  private async apriInviaStatini(): Promise<void> {
    if (this.attributeBoolean('staging')) {
      await Weext.alert('Modalità staging: invio a Statini disabilitato.', { title: 'Attenzione' });
      return;
    }
    if (!this.inviaModalEl) return;

    const result = await statiniApi.anteprima(this.risorsa, this.filtri);
    if (!result.ok) {
      await Weext.alert(result.error, { title: 'Invio non riuscito' });
      return;
    }

    const { daInviare, escluseBozza = 0, giaInviate } = result.data;
    this.setRigaModale('[data-statini-msg]', true,
      `${formatNumero(daInviare)} ${frase(daInviare, 'delega verrà trasmessa', 'deleghe verranno trasmesse')} a Statini.`);
    this.setRigaModale('[data-statini-bozze]', escluseBozza > 0,
      `${formatNumero(escluseBozza)} ${frase(escluseBozza, 'in Cessati/Trasferiti esclusa', 'in Cessati/Trasferiti escluse')}`);
    this.setRigaModale('[data-statini-gia]', giaInviate > 0,
      `${formatNumero(giaInviate)} ${frase(giaInviate, 'già trasmessa esclusa', 'già trasmesse escluse')}`);
    this.setSubmit(daInviare === 0, daInviare === 0 ? 'Nessuna delega da inviare' : 'Invia');

    const mese = this.inviaEl<HTMLSelectElement>('[data-statini-mese]');
    if (mese) mese.value = String(new Date().getMonth() + 1).padStart(2, '0');

    this.inviaModal?.show();
  }

  private async inviaStatini(): Promise<void> {
    const mese = Number(this.inviaEl<HTMLSelectElement>('[data-statini-mese]')?.value);
    const anno = Number(this.inviaEl<HTMLSelectElement>('[data-statini-anno]')?.value);
    if (!mese || !anno) return;

    this.setSubmit(true, 'Invio...', true);
    const result = await statiniApi.invia(this.risorsa, { filtri: this.filtri, mese, anno });
    this.setSubmit(false, 'Invia');

    if (!result.ok) {
      await Weext.alert(result.error, { title: 'Invio non riuscito' });
      return;
    }

    this.inviaModal?.hide();
    this.mostraEsito(result.data);
  }

  private mostraEsito(esito: StatiniEsito): void {
    const body = this.esitoModalEl?.querySelector('[data-esito-body]') as HTMLElement | null;
    if (body) {
      templateParseToElement('invio-statini-esito', body, {
        caricate: esito.caricate,
        codiceInserimento: esito.codiceInserimento,
        errori: esito.errori,
      });
    }
    this.esitoModal?.show();
  }

  protected override onDisconnected(): void {
    this.inviaModal?.dispose();
    this.esitoModal?.dispose();
    this.inviaModalEl?.remove();
    this.esitoModalEl?.remove();
  }
}
